import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Mail, Phone, ShieldCheck } from 'lucide-react';
import { site } from '../data/site';
import { Reveal } from './Reveal';
import { Eyebrow } from './Eyebrow';
const projectTypes = ['Interior Painting', 'Exterior Painting', 'Kitchen Remodeling', 'Bathroom Remodeling', 'Home Remodeling', 'Other'];
const contactMethods = ['Phone', 'Text', 'Email'];
const inputClass = 'w-full rounded-xl border border-navy-900/15 bg-white px-4 py-3.5 text-[15px] text-navy-900 placeholder:text-ink-700/40 transition-colors focus:border-brass focus:outline-none focus:ring-2 focus:ring-brass/25';
const labelClass = 'mb-2 block text-[11px] font-bold uppercase tracking-[0.16em] text-ink-700/60';
export function EstimateForm() {
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
  };
  return <section id="contact" className="bg-cream py-24 sm:py-32" data-id="element-106" data-mp="hav5lcm5kmqq">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-107" data-mp="hdr16uaj2zc0">
        <Reveal data-id="element-108" data-mp="7llbxsbnuw0i">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-109" data-mp="7m3grdce2ik1">
            <Eyebrow data-id="element-110" data-mp="7jh8fbhobz0r">Free Estimate</Eyebrow>
            <h2 className="mt-6 font-display text-[clamp(2rem,4.4vw,3.4rem)] leading-[1.03] tracking-[-0.02em] text-navy-900" data-id="element-111" data-mp="cjwl00u4t2ho">
              Let&rsquo;s Talk About
              <span className="block italic text-brass-dark" data-id="element-112" data-mp="3usnoay0hmp35">Your Project.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ink-700/70" data-id="element-113" data-mp="2k5qw09hgpi4">
              Share a few details and we&rsquo;ll reach out to schedule a walkthrough
              and put together your estimate — no pressure, no obligation.
            </p>

            <ul className="mt-9 space-y-4 text-[15px] text-ink-700/75" data-id="element-114" data-mp="1j21u3telnqr2">
              <li className="flex items-center gap-3" data-id="element-115" data-mp="1o03dygjunu3l">
                <ShieldCheck className="h-5 w-5 text-brass" aria-hidden="true" data-id="element-116" data-mp="1nxqv8169pfph" />
                Free, no-obligation estimates
              </li>
              <li className="flex items-center gap-3" data-id="element-117" data-mp="1hjblar0cufbl">
                <Clock className="h-5 w-5 text-brass" aria-hidden="true" data-id="element-118" data-mp="1hlxt8rr5qwuc" />
                Most homeowners hear back within one business day
              </li>
            </ul>

            <div className="mt-9 space-y-3 border-t border-navy-900/10 pt-8" data-id="element-119" data-mp="17myldbe7v58e">
              <a href={site.phoneHref} className="flex items-center gap-3 text-[15px] font-medium text-navy-900 transition-colors hover:text-brass-dark" data-id="element-120" data-mp="2584dccduw30x">
                <Phone className="h-4 w-4 text-brass" aria-hidden="true" data-id="element-121" data-mp="1vi1kcc6qip2l" />
                {site.phoneDisplay}
              </a>
              <a href={`mailto:${site.email}`} className="flex items-center gap-3 text-[15px] font-medium text-navy-900 transition-colors hover:text-brass-dark" data-id="element-122" data-mp="1qi6t27j1rxh0">
                <Mail className="h-4 w-4 text-brass" aria-hidden="true" data-id="element-123" data-mp="2j73yha25tzsc" />
                {site.email}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} data-id="element-124" data-mp="2vjoi7eeosxe2">
          <div className="rounded-2xl bg-white p-7 shadow-[0_35px_80px_-55px_rgba(13,27,42,0.8)] ring-1 ring-navy-900/5 sm:p-10" data-id="element-125" data-mp="2vkzlld48034n">
            {submitted ? <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} role="status" className="flex min-h-[420px] flex-col items-center justify-center text-center" data-id="element-126" data-mp="35i5aqft9xk28">
                <CheckCircle2 className="h-14 w-14 text-brass" aria-hidden="true" data-id="element-127" data-mp="3j9aftxfev4jy" />
                <h3 className="mt-6 font-display text-3xl text-navy-900" data-id="element-128" data-mp="3o6ion7eo5w8j">
                  Request received.
                </h3>
                <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-ink-700/70" data-id="element-129" data-mp="3t4tz9kcsgart">
                  Thanks for reaching out. A member of our team will contact you shortly
                  to talk through your project and schedule your estimate.
                </p>
                <button type="button" onClick={() => setSubmitted(false)} className="mt-8 rounded-full border border-navy-900/15 px-7 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-navy-900 transition-colors hover:border-brass hover:text-brass-dark" data-id="element-130" data-mp="ts4h4c3bvil5">
                  Submit another request
                </button>
              </motion.div> : <form onSubmit={handleSubmit} noValidate={false} data-id="element-131" data-mp="otupuckq524t">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-132" data-mp="ov5ro6ulzzta">
                  <div data-id="element-133" data-mp="krznr5uyykpz">
                    <label className={labelClass} htmlFor="name" data-id="element-134" data-mp="krg2bf641e77">
                      Name
                    </label>
                    <input id="name" name="name" type="text" required autoComplete="name" placeholder="Your full name" className={inputClass} data-id="element-135" data-mp="20b2sotfq2a6v" />
                  </div>
                  <div data-id="element-136" data-mp="1tt9cw11xsigs">
                    <label className={labelClass} htmlFor="phone" data-id="element-137" data-mp="1ttstnefup0t8">
                      Phone
                    </label>
                    <input id="phone" name="phone" type="tel" required autoComplete="tel" placeholder="(502) 000-0000" className={inputClass} data-id="element-138" data-mp="1ys2ti2cispl6" />
                  </div>
                  <div data-id="element-139" data-mp="2hqf4ltmzs9je">
                    <label className={labelClass} htmlFor="email" data-id="element-140" data-mp="2hpn6ab20fxpf">
                      Email
                    </label>
                    <input id="email" name="email" type="email" required autoComplete="email" placeholder="you@email.com" className={inputClass} data-id="element-141" data-mp="35epz8f23hcz8" />
                  </div>
                  <div data-id="element-142" data-mp="3b2a07aksbwtg">
                    <label className={labelClass} htmlFor="address" data-id="element-143" data-mp="3b1ic0ktip1d7">
                      Project Address
                    </label>
                    <input id="address" name="address" type="text" autoComplete="street-address" placeholder="Street, City, KY" className={inputClass} data-id="element-144" data-mp="362xbekacsfo8" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-145" data-mp="2ncpdcgligfew">
                    <label className={labelClass} htmlFor="projectType" data-id="element-146" data-mp="2ne0fyd37zh4f">
                      Project Type
                    </label>
                    <select id="projectType" name="projectType" required defaultValue="" className={`${inputClass} appearance-none bg-[length:16px] pr-10`} data-id="element-147" data-mp="2ifp9mcc4voqy">
                      <option value="" disabled data-id="element-148" data-mp="xtqwxkl71caj">
                        Select a project type
                      </option>
                      {projectTypes.map(type => <option key={type} value={type} data-id="element-149" data-mp="16vqbm68qk4ab">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2" data-id="element-150" data-mp="f5vjy2xeo2ih">
                    <label className={labelClass} htmlFor="details" data-id="element-151" data-mp="j6xnux29p2qs">
                      What are you looking to have done?
                    </label>
                    <textarea id="details" name="details" rows={5} placeholder="Tell us a little about the space and what you'd like to change." className={`${inputClass} resize-none`} data-id="element-152" data-mp="nyyvg5ohq5ah" />
                  </div>
                  <fieldset className="sm:col-span-2" data-id="element-153" data-mp="2g03ifwc6rzxx">
                    <legend className={labelClass} data-id="element-154" data-mp="2g1mybltxk8z6">Preferred Contact Method</legend>
                    <div className="flex flex-wrap gap-3" data-id="element-155" data-mp="2uye1m66q64u2">
                      {contactMethods.map((method, i) => <label key={method} className="cursor-pointer rounded-full border border-navy-900/15 px-5 py-2.5 text-[13px] text-navy-900 transition-colors hover:border-brass has-[:checked]:border-brass has-[:checked]:bg-brass has-[:checked]:text-navy-900" data-id="element-156" data-mp="2pwyco31ffe6e" data-mpi={i}>
                          <input type="radio" name="contactMethod" value={method} defaultChecked={i === 0} className="sr-only" data-id="element-157" data-mp="38p0zxx8kpspm" />
                          {method}
                        </label>)}
                    </div>
                  </fieldset>
                </div>

                <button type="submit" className="mt-8 w-full rounded-full bg-navy-900 px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:bg-brass hover:text-navy-900 hover:shadow-[0_18px_40px_-18px_rgba(192,149,79,1)]" data-id="element-158" data-mp="a44v3hgq32si">
                  Request My Free Estimate
                </button>
                <p className="mt-4 text-center text-[12px] text-ink-700/50" data-id="element-159" data-mp="3ncr7duojoc9i">
                  We&rsquo;ll only use your information to contact you about your project.
                </p>
              </form>}
          </div>
        </Reveal>
      </div>
    </section>;
}